import { useState, useEffect } from 'react';
import { Save, Plus, Trash2, ImageIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { pageContentService, operationLogService } from '@/services/storage';
import { uploadImage } from '@/services/upload';
import type { PageContent } from '@/types';
import { toast } from 'sonner';

// 图片上传配置 - 无大小限制
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

export default function AdminPageContent() {
  const [pageContent, setPageContent] = useState<PageContent | null>(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [isLoading, setIsLoading] = useState(true);
  const [errorDialog, setErrorDialog] = useState<{open: boolean; title: string; message: string}>({
    open: false,
    title: '',
    message: ''
  });

  useEffect(() => {
    const loadData = async () => {
      const data = await pageContentService.get();
      setPageContent(data);
      setIsLoading(false);
    };
    loadData();
  }, []);

  if (isLoading || !pageContent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const showError = (title: string, message: string) => {
    setErrorDialog({ open: true, title, message });
  };

  const validateImage = (file: File): boolean => {
    // 验证文件类型
    if (!ALLOWED_TYPES.includes(file.type)) {
      showError(
        '文件类型错误',
        `请上传图片文件（支持 ${ALLOWED_TYPES.map(t => t.replace('image/', '').toUpperCase()).join('、')} 格式）`
      );
      return false;
    }

    // 无文件大小限制
    return true;
  };

  const readImageFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onerror = () => {
        reject(new Error('图片读取失败'));
      };

      reader.onloadend = () => {
        const result = reader.result as string;
        if (!result || result.length < 100) {
          reject(new Error('图片处理异常'));
          return;
        }
        resolve(result);
      };

      try {
        reader.readAsDataURL(file);
      } catch (error) {
        reject(new Error('无法读取该图片文件'));
      }
    });
  };

  const handleSave = async () => {
    try {
      console.log('[保存调试] 开始保存...');
      console.log('[保存调试] pageContent:', pageContent ? '有数据' : 'null');
      
      // 确保数据可以被序列化
      const dataToSave = JSON.parse(JSON.stringify(pageContent));
      console.log('[保存调试] 数据序列化成功');
      
      await pageContentService.save(dataToSave);
      console.log('[保存调试] 保存成功');
      
      // 尝试记录操作日志，但失败不阻止主流程
      try {
        await operationLogService.add({
          user: 'admin',
          action: '更新页面内容',
          module: '页面内容',
          details: '更新了页面内容',
        });
      } catch (logError) {
        console.log('[保存调试] 操作日志记录失败（不影响保存）:', logError);
      }
      
      toast.success('页面内容已保存');
    } catch (error: any) {
      console.error('[保存调试] 保存失败:', error);
      console.error('[保存调试] 错误堆栈:', error?.stack);
      showError('保存失败', error?.message || '未知错误');
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[Logo上传调试] 触发', e.target.files);
    const file = e.target.files?.[0];
    if (!file) {
      console.log('[Logo上传调试] 未选择文件');
      return;
    }

    console.log('[Logo上传调试] 文件信息:', file.name, file.type, file.size);

    if (!validateImage(file)) {
      console.log('[Logo上传调试] 验证失败');
      return;
    }

    try {
      console.log('[Logo上传调试] 开始上传...');
      const imageUrl = await uploadImage(file);
      console.log('[Logo上传调试] 上传成功, URL长度:', imageUrl?.length);
      setPageContent(prev => {
        if (!prev) {
          console.log('[Logo上传调试] prev 为 null');
          return null;
        }
        console.log('[Logo上传调试] 更新 logo');
        return { ...prev, logo: imageUrl };
      });
      toast.success('LOGO上传成功');
    } catch (error) {
      console.error('[Logo上传调试] 失败:', error);
      showError('上传失败', error instanceof Error ? error.message : '图片上传失败，请重试');
    }
  };

  const handleImageUpload = async (section: keyof PageContent, field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    console.log(`[通用上传调试] section=${section}, field=${field}, files=`, e.target.files);
    const file = e.target.files?.[0];
    if (!file) {
      console.log('[通用上传调试] 未选择文件');
      return;
    }

    console.log('[通用上传调试] 文件信息:', file.name, file.type, file.size);

    if (!validateImage(file)) {
      console.log('[通用上传调试] 验证失败');
      return;
    }

    try {
      console.log('[通用上传调试] 开始上传...');
      const imageUrl = await uploadImage(file);
      console.log('[通用上传调试] 上传成功, URL长度:', imageUrl?.length);
      
      // 使用函数式更新，确保使用最新的state
      setPageContent(prev => {
        if (!prev) {
          console.log('[通用上传调试] prev 为 null');
          return null;
        }
        console.log(`[通用上传调试] 更新 ${section}.${field}`);
        return {
          ...prev,
          [section]: {
            ...((prev[section] as any) || {}),
            [field]: imageUrl,
          },
        };
      });
      toast.success('图片已上传，请保存');
    } catch (error) {
      console.error('[通用上传调试] 上传失败:', error);
      showError('上传失败', error instanceof Error ? error.message : '图片上传失败，请重试');
    }
  };

  const handlePhilosophyImageUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[经营理念调试] 触发上传', index, e.target.files);
    const file = e.target.files?.[0];
    if (!file) {
      console.log('[经营理念调试] 未选择文件');
      return;
    }

    console.log('[经营理念调试] 文件信息:', file.name, file.type, file.size);

    if (!validateImage(file)) {
      console.log('[经营理念调试] 验证失败');
      return;
    }

    try {
      console.log('[经营理念调试] 开始上传...');
      const imageUrl = await uploadImage(file);
      console.log('[经营理念调试] 上传成功, URL长度:', imageUrl?.length);
      
      // 直接使用函数式更新，不依赖 updatePhilosophyItem
      setPageContent(prev => {
        if (!prev) return null;
        const newItems = [...(prev.philosophy?.items || [])];
        newItems[index] = { ...newItems[index], image: imageUrl };
        console.log('[经营理念调试] 更新 state, 模块', index);
        return {
          ...prev,
          philosophy: {
            ...((prev.philosophy as any) || {}),
            items: newItems,
          },
        };
      });
      toast.success('图片上传成功');
    } catch (error) {
      console.error('[经营理念调试] 上传失败:', error);
      showError('上传失败', error instanceof Error ? error.message : '图片上传失败，请重试');
    }
  };

  const handlePartnerLogoUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[合作伙伴调试] 触发上传', index, e.target.files);
    const file = e.target.files?.[0];
    if (!file) {
      console.log('[合作伙伴调试] 未选择文件');
      return;
    }

    console.log('[合作伙伴调试] 文件信息:', file.name, file.type, file.size);

    if (!validateImage(file)) {
      console.log('[合作伙伴调试] 验证失败');
      return;
    }

    try {
      console.log('[合作伙伴调试] 开始上传...');
      const imageUrl = await uploadImage(file);
      console.log('[合作伙伴调试] 上传成功, URL长度:', imageUrl?.length);
      setPageContent(prev => {
        if (!prev) {
          console.log('[合作伙伴调试] prev 为 null');
          return null;
        }
        const currentLogos = [...(prev.partners?.partnerLogos || [])];
        currentLogos[index] = { ...currentLogos[index], image: imageUrl };
        console.log('[合作伙伴调试] 更新 logo', index);
        return {
          ...prev,
          partners: { ...((prev.partners as any) || {}), partnerLogos: currentLogos },
        };
      });
      toast.success('Logo上传成功');
    } catch (error) {
      console.error('[合作伙伴调试] 失败:', error);
      showError('上传失败', error instanceof Error ? error.message : '图片上传失败，请重试');
    }
  };

  const updateHero = (field: string, value: string) => {
    setPageContent(prev => prev ? ({
      ...prev,
      hero: {
        ...((prev.hero as any) || {}),
        [field]: value,
      },
    }) : null);
  };

  const updateAbout = (field: string, value: any) => {
    setPageContent(prev => prev ? ({
      ...prev,
      about: {
        ...((prev.about as any) || {}),
        [field]: value,
      },
    }) : null);
  };

  const updateAboutContent = (index: number, value: string) => {
    setPageContent(prev => {
      const newContent = [...(prev.about?.content || [])];
      newContent[index] = value;
      return {
        ...prev,
        about: {
          ...((prev.about as any) || {}),
          content: newContent,
        },
      };
    });
  };

  const addAboutParagraph = () => {
    setPageContent(prev => {
      const newContent = [...(prev.about?.content || []), ''];
      return {
        ...prev,
        about: {
          ...((prev.about as any) || {}),
          content: newContent,
        },
      };
    });
  };

  const removeAboutParagraph = (index: number) => {
    setPageContent(prev => {
      const newContent = (prev.about?.content || []).filter((_, i) => i !== index);
      return {
        ...prev,
        about: {
          ...((prev.about as any) || {}),
          content: newContent,
        },
      };
    });
  };

  const updatePhilosophyItem = (index: number, field: string, value: string) => {
    setPageContent(prev => {
      const newItems = [...(prev.philosophy?.items || [])];
      newItems[index] = { ...newItems[index], [field]: value };
      return {
        ...prev,
        philosophy: {
          ...((prev.philosophy as any) || {}),
          items: newItems,
        },
      };
    });
  };

  const logo = pageContent.logo || '';
  const hero = pageContent.hero || {};
  const about = pageContent.about || { content: [] };
  const philosophy = pageContent.philosophy || { items: [] };
  const productsIntro = pageContent.productsIntro || {};
  const news = pageContent.news || {};
  const partners = pageContent.partners || {};
  const footer = pageContent.footer || {};

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">页面内容管理</h1>
        <Button onClick={handleSave}>
          <Save className="mr-2 h-5 w-5" />
          保存所有修改
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-8 flex-wrap">
          <TabsTrigger value="logo">网站LOGO</TabsTrigger>
          <TabsTrigger value="hero">首页Hero</TabsTrigger>
          <TabsTrigger value="about">公司简介</TabsTrigger>
          <TabsTrigger value="philosophy">经营理念</TabsTrigger>
          <TabsTrigger value="products">产品中心</TabsTrigger>
          <TabsTrigger value="partners">合作伙伴</TabsTrigger>
          <TabsTrigger value="footer">页脚配置</TabsTrigger>
          <TabsTrigger value="backgrounds">背景图片</TabsTrigger>
        </TabsList>

        {/* Logo Section */}
        <TabsContent value="logo">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-xl font-bold">网站LOGO</h2>
              <p className="text-gray-500">上传公司LOGO图片，将显示在网站左上角导航栏</p>
              
              <div>
                <Label>
                  <span className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    LOGO图片
                  </span>
                </Label>
                <div className="mt-2">
                  {logo && (
                    <img
                      src={logo}
                      alt="Logo"
                      className="h-20 w-auto object-contain mb-4 bg-gray-100 p-2 rounded"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hero Section */}
        <TabsContent value="hero">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-xl font-bold">首页大图区域</h2>
              
              <div>
                <Label>
                  <span className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    背景图片
                  </span>
                </Label>
                <div className="mt-2">
                  {hero.backgroundImage && (
                    <div className="relative">
                      <img
                        src={hero.backgroundImage}
                        alt="Hero"
                        className="w-full h-48 object-cover rounded mb-2"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => updateHero('backgroundImage', '')}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        删除
                      </Button>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('hero', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              <div>
                <Label htmlFor="heroTitle">主标题</Label>
                <textarea
                  id="heroTitle"
                  className="w-full border rounded-md px-3 py-2"
                  rows={4}
                  value={hero.title || ''}
                  onChange={(e) => updateHero('title', e.target.value)}
                  placeholder="输入主标题，使用换行分隔多行"
                />
                <p className="text-xs text-gray-500 mt-1">使用换行符分隔多行文字</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="titleFontSize">标题字体大小 (px)</Label>
                  <Input
                    id="titleFontSize"
                    type="number"
                    value={hero.titleFontSize || 48}
                    onChange={(e) => updateHero('titleFontSize', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="titleLineHeight">标题行高</Label>
                  <Input
                    id="titleLineHeight"
                    type="number"
                    step="0.1"
                    value={hero.titleLineHeight || 1.4}
                    onChange={(e) => updateHero('titleLineHeight', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="heroSubtitle">副标题</Label>
                <Input
                  id="heroSubtitle"
                  value={hero.subtitle || ''}
                  onChange={(e) => updateHero('subtitle', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="subtitleFontSize">副标题字体大小 (px)</Label>
                <Input
                  id="subtitleFontSize"
                  type="number"
                  value={hero.subtitleFontSize || 20}
                  onChange={(e) => updateHero('subtitleFontSize', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="titleColor">主标题颜色</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={hero.titleColor || '#ffffff'}
                      onChange={(e) => updateHero('titleColor', e.target.value)}
                      className="w-10 h-10 rounded"
                    />
                    <Input
                      id="titleColor"
                      value={hero.titleColor || '#ffffff'}
                      onChange={(e) => updateHero('titleColor', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="subtitleColor">副标题颜色</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={hero.subtitleColor || '#ffffff'}
                      onChange={(e) => updateHero('subtitleColor', e.target.value)}
                      className="w-10 h-10 rounded"
                    />
                    <Input
                      id="subtitleColor"
                      value={hero.subtitleColor || '#ffffff'}
                      onChange={(e) => updateHero('subtitleColor', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="buttonText">按钮文字</Label>
                  <Input
                    id="buttonText"
                    value={hero.buttonText || ''}
                    onChange={(e) => updateHero('buttonText', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="buttonLink">按钮链接</Label>
                  <Input
                    id="buttonLink"
                    value={hero.buttonLink || ''}
                    onChange={(e) => updateHero('buttonLink', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* About Section */}
        <TabsContent value="about">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-xl font-bold">公司简介</h2>

              <div>
                <Label htmlFor="aboutTitle">标题</Label>
                <Input
                  id="aboutTitle"
                  value={about.title || ''}
                  onChange={(e) => updateAbout('title', e.target.value)}
                />
              </div>

              <div>
                <Label>媒体类型</Label>
                <div className="flex gap-4 mt-2 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="aboutMediaType"
                      checked={!about.video}
                      onChange={() => {
                        updateAbout('video', '');
                      }}
                    />
                    <span>图片</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="aboutMediaType"
                      checked={!!about.video}
                      onChange={() => {
                        updateAbout('image', '');
                      }}
                    />
                    <span>视频</span>
                  </label>
                </div>

                {!about.video ? (
                  <div>
                    <Label>
                      <span className="flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        公司图片
                      </span>
                    </Label>
                    <div className="mt-2">
                      {about.image && (
                        <img
                          src={about.image}
                          alt="About"
                          className="w-full h-48 object-cover rounded mb-2"
                        />
                      )}
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload('about', 'image', e)}
                      />
                      <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>
                      <span className="flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        公司视频
                      </span>
                    </Label>
                    <div className="mt-2">
                      {about.video && (
                        <video
                          src={about.video}
                          controls
                          className="w-full h-48 object-cover rounded mb-2"
                        />
                      )}
                      <Input
                        type="file"
                        accept="video/*"
                        onChange={(e) => handleImageUpload('about', 'video', e)}
                      />
                      <p className="text-xs text-gray-500 mt-1">支持 MP4、WebM 格式，最大 20MB</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="aboutFontSize">正文字体大小 (px)</Label>
                  <Input
                    id="aboutFontSize"
                    type="number"
                    value={about.fontSize || 16}
                    onChange={(e) => updateAbout('fontSize', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="aboutLineHeight">正文行高</Label>
                  <Input
                    id="aboutLineHeight"
                    type="number"
                    step="0.1"
                    value={about.lineHeight || 1.8}
                    onChange={(e) => updateAbout('lineHeight', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label>正文内容</Label>
                <div className="space-y-2 mt-2">
                  {(about.content || []).map((paragraph: string, index: number) => (
                    <div key={index} className="flex items-center space-x-2">
                      <textarea
                        className="flex-1 border rounded-md px-3 py-2"
                        rows={3}
                        value={paragraph}
                        onChange={(e) => updateAboutContent(index, e.target.value)}
                        placeholder={`段落 ${index + 1}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAboutParagraph(index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addAboutParagraph}>
                    <Plus className="mr-1 h-4 w-4" />
                    添加段落
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Philosophy Section */}
        <TabsContent value="philosophy">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-xl font-bold">经营理念</h2>

              <div>
                <Label htmlFor="philosophyTitle">标题</Label>
                <Input
                  id="philosophyTitle"
                  value={philosophy.title || ''}
                  onChange={(e) => setPageContent(prev => prev ? ({
                    ...prev,
                    philosophy: { ...((prev.philosophy as any) || {}), title: e.target.value },
                  }) : null)}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {(philosophy.items || []).map((item: any, index: number) => (
                  <Card key={index}>
                    <CardContent className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold">模块 {index + 1}</h3>
                      </div>

                      <div>
                        <Label>
                          <span className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            背景图片
                          </span>
                        </Label>
                        <div className="mt-2">
                          {item.image && (
                            <div className="relative">
                              <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-24 object-cover rounded mb-2"
                              />
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="absolute top-1 right-1"
                                onClick={() => updatePhilosophyItem(index, 'image', '')}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePhilosophyImageUpload(index, e)}
                          />
                          <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                        </div>
                      </div>

                      <div>
                        <Label>标题</Label>
                        <Input
                          value={item.title || ''}
                          onChange={(e) => updatePhilosophyItem(index, 'title', e.target.value)}
                        />
                      </div>

                      <div>
                        <Label>描述</Label>
                        <textarea
                          className="w-full border rounded-md px-3 py-2"
                          rows={2}
                          value={item.description || ''}
                          onChange={(e) => updatePhilosophyItem(index, 'description', e.target.value)}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>字体大小 (px)</Label>
                          <Input
                            type="number"
                            value={item.fontSize || 16}
                            onChange={(e) => updatePhilosophyItem(index, 'fontSize', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>文字颜色</Label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="color"
                              value={item.textColor || '#ffffff'}
                              onChange={(e) => updatePhilosophyItem(index, 'textColor', e.target.value)}
                              className="w-10 h-10 rounded"
                            />
                            <Input
                              value={item.textColor || '#ffffff'}
                              onChange={(e) => updatePhilosophyItem(index, 'textColor', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products Intro Section */}
        <TabsContent value="products">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-xl font-bold">产品中心介绍</h2>

              <div>
                <Label htmlFor="productsTitle">标题</Label>
                <Input
                  id="productsTitle"
                  value={productsIntro.title || ''}
                  onChange={(e) => setPageContent(prev => prev ? ({
                    ...prev,
                    productsIntro: { ...((prev.productsIntro as any) || {}), title: e.target.value },
                  }) : null)}
                />
              </div>

              <div>
                <Label htmlFor="productsDescription">描述</Label>
                <textarea
                  id="productsDescription"
                  className="w-full border rounded-md px-3 py-2"
                  rows={4}
                  value={productsIntro.description || ''}
                  onChange={(e) => setPageContent(prev => prev ? ({
                    ...prev,
                    productsIntro: { ...((prev.productsIntro as any) || {}), description: e.target.value },
                  }) : null)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Footer Section */}
        <TabsContent value="footer">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-xl font-bold">页脚配置</h2>
              <p className="text-gray-500">设置网站底部显示的公司Logo和二维码</p>

              {/* Footer Company Logo */}
              <div>
                <Label className="font-bold">页脚公司Logo</Label>
                <p className="text-sm text-gray-500 mb-2">显示在页脚左侧公司名称旁</p>
                <div className="mt-2">
                  {footer.companyLogo && (
                    <img
                      src={footer.companyLogo}
                      alt="Footer Company Logo"
                      className="w-48 h-auto object-contain bg-gray-100 rounded p-2 mb-4"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('footer', 'companyLogo', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式</p>
                </div>
              </div>

              {/* WeChat QR Code */}
              <div>
                <Label className="font-bold">微信公众号二维码</Label>
                <p className="text-sm text-gray-500 mb-2">显示在页脚右侧，用于扫码关注</p>
                <div className="mt-2">
                  {footer.qrCode && (
                    <img
                      src={footer.qrCode}
                      alt="WeChat QR Code"
                      className="w-32 h-32 object-contain bg-white rounded p-2 mb-4"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('footer', 'qrCode', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，建议正方形图片</p>
                </div>
              </div>

              {/* Footer Background */}
              <div>
                <Label className="font-bold">页脚背景图片（可选）</Label>
                <div className="mt-2">
                  {footer.backgroundImage && (
                    <img
                      src={footer.backgroundImage}
                      alt="Footer Background"
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('footer', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">如不设置则使用默认深色背景</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Partners Section */}
        <TabsContent value="partners">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-xl font-bold">合作伙伴Logo管理</h2>
              <p className="text-gray-500">上传合作伙伴的品牌Logo图片，将显示在首页合作伙伴区域</p>

              <div className="space-y-4">
                {(partners.partnerLogos || []).map((partner: any, index: number) => (
                  <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg">
                    <div className="w-24 h-12 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                      {partner.image ? (
                        <img src={partner.image} alt={partner.name} className="max-w-full max-h-full object-contain" />
                      ) : (
                        <span className="text-gray-400 text-xs">无图片</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <Input
                        value={partner.name}
                        onChange={(e) => {
                          const newLogos = [...(partners.partnerLogos || [])];
                          newLogos[index] = { ...newLogos[index], name: e.target.value };
                          setPageContent(prev => prev ? ({
                            ...prev,
                            partners: { ...((prev.partners as any) || {}), partnerLogos: newLogos },
                          }) : null);
                        }}
                        placeholder="合作伙伴名称"
                      />
                    </div>
                    <div>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePartnerLogoUpload(index, e)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newLogos = (partners.partnerLogos || []).filter((_, i) => i !== index);
                        setPageContent(prev => prev ? ({
                          ...prev,
                          partners: { ...((prev.partners as any) || {}), partnerLogos: newLogos },
                        }) : null);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => {
                    const newLogos = [...(partners.partnerLogos || []), { name: '', image: '' }];
                    setPageContent(prev => prev ? ({
                      ...prev,
                      partners: { ...((prev.partners as any) || {}), partnerLogos: newLogos },
                    }) : null);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  添加合作伙伴
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Background Images Section */}
        <TabsContent value="backgrounds">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-xl font-bold">各板块背景图片</h2>
              <p className="text-gray-500">上传各板块的背景图片，如不设置则使用默认背景</p>

              {/* Hero Background */}
              <div>
                <Label className="font-bold">首页大图背景</Label>
                <div className="mt-2">
                  {hero.backgroundImage && (
                    <img
                      src={hero.backgroundImage}
                      alt="Hero Background"
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('hero', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* About Background */}
              <div>
                <Label className="font-bold">关于我们背景</Label>
                <div className="mt-2">
                  {about.backgroundImage && (
                    <img
                      src={about.backgroundImage}
                      alt="About Background"
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('about', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* Philosophy Background */}
              <div>
                <Label className="font-bold">经营理念背景</Label>
                <div className="mt-2">
                  {philosophy.backgroundImage && (
                    <img
                      src={philosophy.backgroundImage}
                      alt="Philosophy Background"
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('philosophy', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* Products Background */}
              <div>
                <Label className="font-bold">产品中心背景</Label>
                <div className="mt-2">
                  {productsIntro.backgroundImage && (
                    <img
                      src={productsIntro.backgroundImage}
                      alt="Products Background"
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('productsIntro', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* News Background */}
              <div>
                <Label className="font-bold">新闻中心背景</Label>
                <div className="mt-2">
                  {news.backgroundImage && (
                    <img
                      src={news.backgroundImage}
                      alt="News Background"
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('news', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* Partners Background */}
              <div>
                <Label className="font-bold">合作伙伴背景</Label>
                <div className="mt-2">
                  {partners.backgroundImage && (
                    <img
                      src={partners.backgroundImage}
                      alt="Partners Background"
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('partners', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* Footer Background */}
              <div>
                <Label className="font-bold">页脚背景</Label>
                <div className="mt-2">
                  {footer.backgroundImage && (
                    <img
                      src={footer.backgroundImage}
                      alt="Footer Background"
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('footer', 'backgroundImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* ISO Certification Image */}
              <div>
                <Label className="font-bold">ISO9001质量体系认证图片</Label>
                <p className="text-sm text-gray-500">显示在技术与支持→质量体系页面</p>
                <div className="mt-2">
                  {(pageContent.qualitySystem?.isoImage) && (
                    <img
                      src={pageContent.qualitySystem?.isoImage}
                      alt="ISO Certification"
                      className="w-full h-32 object-contain bg-white rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('qualitySystem', 'isoImage', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* WeChat QR Code */}
              <div>
                <Label className="font-bold">微信公众号二维码</Label>
                <p className="text-sm text-gray-500">显示在页脚公司信息下方</p>
                <div className="mt-2">
                  {footer.qrCode && (
                    <img
                      src={footer.qrCode}
                      alt="WeChat QR Code"
                      className="w-32 h-32 object-contain bg-white rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('footer', 'qrCode', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>

              {/* Footer Company Logo */}
              <div>
                <Label className="font-bold">页脚公司Logo</Label>
                <p className="text-sm text-gray-500">显示在页脚公司名称旁，如不设置则使用默认图标</p>
                <div className="mt-2">
                  {footer.companyLogo && (
                    <img
                      src={footer.companyLogo}
                      alt="Company Logo"
                      className="w-24 h-24 object-contain bg-white rounded mb-2"
                    />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('footer', 'companyLogo', e)}
                  />
                  <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF、WebP 格式，最大 5MB</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Error Dialog */}
      <Dialog open={errorDialog.open} onOpenChange={(open) => setErrorDialog({ ...errorDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {errorDialog.title}
            </DialogTitle>
            <DialogDescription>
              {errorDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setErrorDialog({ ...errorDialog, open: false })}>
              知道了
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
